import { useEffect, useState, useCallback } from 'react';
import { Modal } from '@/components/ui/modal';
import { useMediaStore } from '@/store/mediaStore';
import type { MediaItem } from '@/lib/api-services/media-service';

export interface MediaPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the selected image URL. The modal closes automatically. */
  onSelect: (url: string) => void;
}

// ── Picker item (no drag-and-drop, click-to-select) ──────────────────────────

interface PickerMediaItemProps {
  item: MediaItem;
  onSelect: (url: string) => void;
  onDelete: (item: MediaItem) => void;
}

function PickerMediaItem({ item, onSelect, onDelete }: PickerMediaItemProps) {
  return (
    <div
      className="lw-picker-media-item group relative aspect-square cursor-pointer overflow-hidden rounded-md border border-border bg-card transition-colors hover:border-primary"
      onClick={() => onSelect(item.url)}
      title={item.fileName}
    >
      <img
        src={item.url}
        alt={item.fileName}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
      />

      {/* Hover overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
        {/* Use image badge */}
        <div className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
          Use image
        </div>

        {/* Delete button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item);
          }}
          className="rounded-md bg-destructive p-1.5 text-destructive-foreground transition-colors hover:bg-destructive/90"
          title="Delete"
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      {/* Filename bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
        <p className="truncate text-xs text-white" title={item.fileName}>
          {item.fileName}
        </p>
      </div>
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────────────────

export function MediaPickerModal({
  isOpen,
  onClose,
  onSelect,
}: MediaPickerModalProps) {
  const { items, isLoading, isUploading, error, fetchMedia, uploadMedia, deleteMedia } =
    useMediaStore();

  const [isDragging, setIsDragging] = useState(false);

  // Fetch on open (no-op if already cached)
  useEffect(() => {
    if (isOpen) fetchMedia();
  }, [isOpen, fetchMedia]);

  const handleSelect = (url: string) => {
    onSelect(url);
    onClose();
  };

  const handleDelete = async (item: MediaItem) => {
    if (!confirm(`Delete "${item.fileName}"?`)) return;
    await deleteMedia(item);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    await uploadMedia(Array.from(files));
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFileUpload(e.dataTransfer.files);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Media Library" size="xl">
      {/* Upload zone */}
      <div
        className={`relative mb-4 rounded-lg border-2 border-dashed p-6 transition-colors ${
          isDragging
            ? 'border-primary bg-primary/10'
            : 'border-border hover:border-primary/50'
        }`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="media-picker-upload"
          className="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp"
          multiple
          onChange={(e) => handleFileUpload(e.target.files)}
          disabled={isUploading}
        />
        <label
          htmlFor="media-picker-upload"
          className="flex cursor-pointer flex-col items-center justify-center"
        >
          <svg
            className="mb-2 h-10 w-10 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          <p className="text-center text-sm text-muted-foreground">
            {isUploading ? (
              <span className="text-primary">Uploading...</span>
            ) : (
              <>
                <span className="font-medium">Click to upload</span> or drag
                and drop
                <br />
                <span className="text-xs">PNG, JPG, GIF up to 10MB</span>
              </>
            )}
          </p>
        </label>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Media grid */}
      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <span className="text-sm text-muted-foreground">
            Loading media...
          </span>
        </div>
      ) : items.length === 0 ? (
        <div className="flex h-48 items-center justify-center">
          <span className="text-sm text-muted-foreground">
            No media files yet. Upload some images to get started.
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {items.map((item) => (
            <PickerMediaItem
              key={item.url}
              item={item}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
